import React from 'react';
import { Link } from 'react-router-dom';
import heroBg from '../images/hero-bg.jpg';
import { usePageContent } from '../hooks/usePageContent';
import Icon from './icons/Icon';
import StarRating from './icons/StarRating';
import './HeroSection.css';

// Default values (fallback if no content in Firestore)
const DEFAULT_HERO_CONTENT = {
  subtitle: 'Dr. Muyabins Dental Care',
  title: 'Elevating Smiles with Expert Care and a Gentle Touch',
  description: 'Experience premium dental care in a comfortable, modern environment. We combine advanced technology with personalized attention to give you the smile you deserve.',
  rating: '',
  ratingLabel: 'MCT Registered',
  ratingText: '5 Years of Dental Experience',
  emergencyPhone: '+255 755 214 841',
  openingHours: 'Mon - Sat: 08:00 - 20:00',
  email: 'contact@dr.muyabin.com'
};

const sanitizeHeroContent = (content) => {
  const sanitized = { ...DEFAULT_HERO_CONTENT, ...content };

  // Override legacy placeholder copy saved in Firestore/CMS
  if (/23k/i.test(sanitized.ratingText || '')) {
    sanitized.ratingText = DEFAULT_HERO_CONTENT.ratingText;
  }
  if (sanitized.ratingLabel === 'Google Rating' && !sanitized.rating) {
    sanitized.ratingLabel = DEFAULT_HERO_CONTENT.ratingLabel;
  }

  return sanitized;
};

const HeroSection = () => {
  const { content: rawContent } = usePageContent('home', 'hero', DEFAULT_HERO_CONTENT);
  const content = sanitizeHeroContent(rawContent);

  return (
    <div 
      className="hero-section"
      style={{ backgroundImage: `url(${heroBg})` }}
    >
      {/* Gradient Overlay */}
      <div className="hero-overlay"></div>

      {/* Hero Content */}
      <div className="hero-content">
        <span className="hero-subtitle">{content.subtitle}</span>
        <h1 className="hero-title">
          {content.title}
        </h1>
        <p className="hero-description">
          {content.description}
        </p>
        
        <div className="hero-buttons">
          <Link to="/appointment" className="btn btn-primary">
            Book Appointment
          </Link>
          <Link to="/services" className="btn btn-secondary">
            Our Services
          </Link>
        </div>

        <div className="hero-trust-chips">
          <span className="hero-trust-chip">
            <Icon name="award" size={14} /> MCT Registered
          </span>
          <span className="hero-trust-chip">
            <Icon name="timer" size={14} /> 5 Years Experience
          </span>
          <span className="hero-trust-chip">
            <Icon name="mapPin" size={14} /> Dar es Salaam
          </span>
        </div>

        {/* Trust badges */}
        {(content.rating || content.ratingLabel || content.ratingText) && (
          <div className="hero-rating">
            <StarRating count={5} size={16} className="rating-stars" />
            {content.rating && (
              <>
                <span className="rating-number">{content.rating}</span>
                {(content.ratingLabel || content.ratingText) && (
                  <span style={{ opacity: 0.8 }}>•</span>
                )}
              </>
            )}
            {content.ratingLabel && <span>{content.ratingLabel}</span>}
            {content.ratingLabel && content.ratingText && (
              <span style={{ opacity: 0.8 }}>•</span>
            )}
            {content.ratingText && <span>{content.ratingText}</span>}
          </div>
        )}
      </div>

      {/* Info Bar */}
      <div className="hero-info-bar">
        <div className="hero-info-container">
          <div className="hero-info-item">
            <div className="hero-info-icon"><Icon name="phone" size={24} /></div>
            <div className="hero-info-content">
              <h4>Emergency Contact</h4>
              <p>{content.emergencyPhone}</p>
            </div>
          </div>

          <div className="hero-info-item">
            <div className="hero-info-icon"><Icon name="clock" size={24} /></div>
            <div className="hero-info-content">
              <h4>Opening Hours</h4>
              <p>{content.openingHours}</p>
            </div>
          </div>

          <div className="hero-info-item">
            <div className="hero-info-icon"><Icon name="mail" size={24} /></div>
            <div className="hero-info-content">
              <h4>Email Us</h4>
              <p>{content.email}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;
